/**
 * The ch04 feature manifest — the in-season workbench.
 *
 * reqs/04 §1 is emphatic that this chapter "is not five products — it is **one weekly
 * (moving to daily) decision cycle** with five decision classes hanging off a shared
 * forecast and a shared inventory position". The route set is shaped to that: the
 * workbench is the entry point and the only inbox, and the five subject screens are
 * where a planner goes when the queue sends them somewhere.
 *
 * | Route | reqs/04 | What it is for |
 * |---|---|---|
 * | `network-health` | DEC-107 | NETWORK HEALTH — every served combination of the run, its states and interventions (level 0) |
 * | `network-health/by` | DEC-107 | WHERE — one row per department, store or article (`?by=`), by uncovered problems (level 1) |
 * | `network-health/article/:articleKey` | DEC-107 | ONE ARTICLE — its projection, its stock, every store (level 2) |
 * | `network-health/store/:storeKey` | DEC-107 | ONE STORE — every article it holds (level 2, «Raggruppa per Negozio») |
 * | `network-health/store/:storeKey/article/:articleKey` | DEC-107 | SIZES — the whole scale of one store × article (level 3) |
 * | `network-health/doors` | issue 308 | DOORS — weeks of cover against size availability, one dot per door, over the run's 13 weeks (ADR 6015) |
 * | `service-exchange` | issue 308 | SERVICE EXCHANGE — pairs held against service, per rounding rule and level, from a forecast backtest (ADR 6015) |
 * | `workbench` | §1 | ALERTS — the alert inbox, one alert decided at a time |
 * | `workbench/proposals` | reqs/07 §3.5 | PROPOSALS — the run's proposals as one queue, one line or one reason group decided at a time |
 * | `forecast` | §4.1 | Accuracy, signed bias and the winner/loser reclassification |
 * | `allocation` | §4.2 | The proposal grid and the constraint that bound each line |
 * | `allocation-why-not-sent` | §4.2 | WHY NOT SENT — «Perché»'s second part: the need left without a line per store × article, and what each size lost to (2026-10-24) |
 * | `allocation-cells` | §4.2 | EVERY CELL OF THE RUN — the cells counted by outcome and the file of every one; Proposals' second part (2026-10-24) |
 * | `open-to-buy` | §4.3 | The recomputed open position, its bridge, and chase/cancel/defer as scenarios |
 * | `markdown` | §4.4 | RUN — the cycle run, the objective and the options a solve is posed with |
 * | `markdown/output` | §4.4 | RESULT — the answer to the question posed: the path, the objective comparison, several options |
 * | `markdown/frontier` | §4.4 | FRONTIER — margin against cash for the option posed; a strategy chosen opens Result (issue 226) |
 * | `markdown/approvals` | §4.4 | PENDING APPROVALS — the run's price steps and the decision recorded on each |
 * | `markdown/calendar` | §4.4 | THE RUN'S CALENDAR — what the price run recommended, week by week, and why articles are missing (issue 226) |
 * | `markdown/stock` | §4.4 | STOCK POSITION — the store position a solve opens from |
 * | `markdown/explanation` | §4.4 | EXPLANATION — the fitted elasticities by cluster, channel and category, and over time |
 * | `markdown/configurations` | §4.4 | SAVED CONFIGURATIONS — the list of saved questions (ADR 6002) |
 * | `markdown/configurations/compare` | §4.4 | COMPARISON — two saved versions, parameter by parameter (issue 226) |
 * | `markdown/configurations/:configurationId` | §4.4 | CONFIGURATION — one saved question: versions, recall, archive (issue 226) |
 * | `rebalancing` | §4.5 | TRANSFERS — the proposals and size-run repairs, decided per action type or by filter |
 * | `transfer-why` | §4.5 | WHY — one transfer's recorded reasons (`?row=<transfer_id>`) |
 * | `cost-to-serve` | §4.5, reqs/08 §8 | COST TO SERVE — gate D2 per market (`?asOf=`) |
 * | `compacting-simulation` | platform ADR 9056 | COMPACTING SIMULATION — the one run asked again over one run scope, with its own parameters |
 * | `compacting-simulation/history` | platform ADR 9056 | its HISTORY — the market's simulations |
 * | `compacting-simulation/:simulationId` | platform ADR 9056 | ONE SIMULATION — state, question, its run, the compare with the live run |
 * | `compacting-simulation/:simulationId/propose` | ADR 9081 (2026-11-03) | PROPOSE — its overlay as a parameter proposal at its market, one per owner |
 * | `simulate` | #148 5.3 | COMPOSE — one stage of a live run, set up for a scenario branch |
 * | `simulate/history` | #148 5.3 | HISTORY — the base run's simulations |
 * | `simulate/:simulationId` | #148 5.3 | RESULTS — one simulation, compared with the live run |
 * | `simulate/:simulationId/propose` | #148 5.3 | PROPOSE — its parameters as a registry change |
 * | `campaigns` | bata-reqs#33, ADR 6017 | CAMPAIGN COMPOSE — an advisory campaign simulation of two or more alternatives |
 * | `campaigns/history` | ADR 6017 | CAMPAIGN HISTORY — the base run's campaign simulations |
 * | `campaigns/:simulationId` | ADR 6017 | CAMPAIGN RESULTS — one campaign simulation's answer, compared on realised margin |
 * | `write-back` | §14 §5 O4 | DELIVERY LOG — delivery state of approved commands, uncollapsed |
 * | `write-back/pack` | ADR 6037 | EXECUTION PACK — one run's approved lines to execute, and the planner's two reports |
 * | `write-back/backlog` | ADR 6104 | BACKLOG — what the run's market still owes, from every run |
 * | `cycles` | ADR 9016 | CYCLES, STATUS — whether each decision cycle ran for the run's market, what it wrote, when it runs next |
 * | `cycles/findings` | ADR 9019 / 9100 | CYCLES, FINDINGS — what the run's digest found, grouped by department family or by store (`?by=store`), with the drill into families and stores; `?module=exit` reads the exit consolidation run's (issue 226) |
 * | `cycles/mix` | ADR 9019 / 9100 | CYCLES, MIX — what the run wrote, by kind: four tiles, the expected impact and the store figures (issue 226) |
 * | `cycles/trend` | ADR 9100 | CYCLES, TREND — the digests' headline figures cycle by cycle, for the run's market |
 * | `run-scopes` | bata-reqs ADR 1003, ADR 6028 | «PERIMETRI» — the market's library of run Scopes, store selections and product selections |
 * | `run-scopes/edit` | ADR 6028 | EDITOR — a Scope's include and exclude rules or a selection's condition, live counts, saved as a change set |
 * | `run-scopes/changes/:changeId` | ADR 6028 | CHANGE — what one library change set does: counts before and after, where used |
 * | `run-scopes/:kind/:entryId` | ADR 6028 | ENTRY — one entry's rules, what it covers today, where it is used, its versions |
 *
 * Workbench and Write-back each carry their pages as `children` of one navigation entry, drawn as
 * sub-entries in the rail (client rules 1 and 3, package A 2026-09-30). The parent's URL stays the
 * first page, so every link made before the split still opens. Cycles is new: its three pages were
 * sections of the Cockpit until DEC-87 made the Cockpit synthesis only.
 *
 * Every route declares `exports` and `drill`, because the manifest types make both
 * required on a data route — G2 tests *d* and *f* are type errors here rather than
 * census failures later.
 */

import { defineFeature } from '../manifest';

import {
  accuracyExport,
  allocationWhyExport,
  bridgeExport,
  costToServeExport,
  coverExport,
  departmentLinesExport,
  elasticityExport,
  exceptionQueueExport,
  markdownCalendarExport,
  markdownFrontierExport,
  markdownResultExport,
  priceActionQueueExport,
  proposalExport,
  runConfigurationComparisonExport,
  runConfigurationsExport,
  runConfigurationVersionsExport,
  stockPositionExport,
  runProvenanceExport,
  salesHistoryExport,
  storeArticleExport,
  storeLinesExport,
  transferExport,
  transferWhyExport,
  writeBackExport,
} from './exports';
import { INSEASON_GUIDES } from './guides';
import { MOVE_LIST_EXPORT_ID, MOVE_WORKBOOK_EXPORT_ID, moveListExport, moveWorkbookExport } from './compacting/move-workbook';
import { COMPACTING_SIMULATION_HISTORY_EXPORT_ID, compactingSimulationListExport } from './compacting/simulation';
import { cellsExport, groupsExport, networkExport } from './network-health/exports';
import { doorsExport } from './network-health/doors';
import { serviceExchangeExport } from './service-exchange/exports';
import { simulationCompareExport, simulationListExport } from './simulate';
import { campaignHistoryExport, campaignResultsExport } from './campaigns/exports';
import { changeExport, CHANGE_EXPORT_ID, historyExport, HISTORY_EXPORT_ID, libraryExport, LIBRARY_EXPORT_ID } from './run-scopes/exports';
import { backlogExport, executionPackExport } from './write-back-exports';
import { runCellsExport, RUN_CELLS_EXPORT_ID } from './run-cells';
import { notSentExport, NOT_SENT_EXPORT_ID } from './not-sent';
import {
  CYCLE_FINDINGS_EXPORT_ID, CYCLE_MIX_EXPORT_ID, CYCLE_STATUS_EXPORT_ID, CYCLE_TREND_EXPORT_ID, cycleFindingsExport, cycleMixExport,
  cycleStatusExport, cycleTrendExport,
} from './cycles-exports';

export default defineFeature({
  slug: 'inseason',
  titleKey: 'inseason.title',
  navLabelKey: 'inseason.navLabel',
  icon: 'activity',  // brand guide p.14
  railGroup: 'active',  // bata-ui#132 — a module planners run decisions in today
  localeNamespace: 'inseason',
  // Every route's guide and guided tour (ADR 9044) — one entry per route below, or the census fails.
  guides: INSEASON_GUIDES,
  // The five screens offered to a planner, in the order the decision is made — then the
  // two that are not offered yet, grouped after them rather than left in their old
  // positions. An inert entry between two working ones reads as a gap in the sequence;
  // the same entry after them reads as what it is, the part still to come.
  //
  // `write-back` was in that withheld group and is not any more, so it goes back into
  // decision order rather than staying parked at the end: the grouping was a consequence
  // of being withheld, and an entry kept there after it works would say the screen is
  // still to come. It sits last of the five because it IS last in the cycle — the queue,
  // the three decision classes, and then what became of the commands those decisions
  // raised (§14 §5 O4). `record_price_action_dispositions` mints a command in the same
  // transaction as the price decision, so the read behind it has a producer and an empty
  // page here is a fact about the estate rather than about the wiring.
  navigation: [
    // DEC-107 (brief part 4 §7): the network-health cockpit, Riassortimento's FIRST entry, with no
    // counter — it is the starting point of the checks and the one entry that is not a queue.
    { labelKey: 'inseason.nav.networkHealth', to: '/inseason/network-health', order: 39 },
    // Alerts and Proposals: one page each (client rule 3), sub-entries of Workbench (rule 1).
    {
      labelKey: 'inseason.nav.workbench',
      to: '/inseason/workbench',
      order: 40,
      children: [
        { labelKey: 'inseason.nav.workbenchAlerts', to: '/inseason/workbench', order: 1 },
        { labelKey: 'inseason.nav.workbenchProposals', to: '/inseason/workbench/proposals', order: 2 },
      ],
    },
    // One page, one function (client rule 3, DEC-88, ADR 6006 amendment 2026-09-30): the
    // allocation screen's proposals, "why this" and stock are three pages, drawn as Allocation's
    // sub-entries in the rail. Sibling prefixes, because `allocation/:storeKey` owns every second
    // segment under `allocation/` (`allocation-pages.tsx`).
    {
      labelKey: 'inseason.nav.allocation',
      to: '/inseason/allocation',
      order: 41,
      children: [
        { labelKey: 'inseason.nav.allocationProposals', to: '/inseason/allocation', order: 1 },
        { labelKey: 'inseason.nav.allocationWhy', to: '/inseason/allocation-why', order: 2 },
        { labelKey: 'inseason.nav.allocationStock', to: '/inseason/allocation-stock', order: 3 },
      ],
    },
    // The decision on the run's lines — Replenishment's "Pending approvals" (client-rules audit §4).
    { labelKey: 'inseason.nav.allocationDecisions', to: '/inseason/allocation-decisions', order: 41.5 },
    // bata-ui issue 308 (ADR 6015): the service exchange curve — what each stocking level of the
    // forecast would have cost in pairs and served. Replenishment's, after its decisions.
    { labelKey: 'inseason.nav.serviceExchange', to: '/inseason/service-exchange', order: 41.6 },
    // The views of one question (planner review, 2026-09-25), nested under the screen they
    // belong to — one function each since client rule 3 (2026-09-30, DEC-88): the question, its
    // answer, the margin-versus-cash frontier (issue 226), the stock it opens from, the evidence,
    // and the saved questions. Six: the run's calendar is under Pending approvals (owner ruling
    // 2026-10-07).
    {
      labelKey: 'inseason.nav.markdown',
      to: '/inseason/markdown',
      order: 42,
      children: [
        { labelKey: 'inseason.nav.markdownRun', to: '/inseason/markdown', order: 1 },
        { labelKey: 'inseason.nav.markdownOutput', to: '/inseason/markdown/output', order: 2 },
        { labelKey: 'inseason.nav.markdownFrontier', to: '/inseason/markdown/frontier', order: 3 },
        { labelKey: 'inseason.nav.markdownStock', to: '/inseason/markdown/stock', order: 4 },
        { labelKey: 'inseason.nav.markdownExplanation', to: '/inseason/markdown/explanation', order: 5 },
        { labelKey: 'inseason.nav.markdownConfigurations', to: '/inseason/markdown/configurations', order: 6 },
      ],
    },
    // The run's price decisions — Pricing's "Pending approvals" in the rail (the client-rules
    // audit §4). A screen of its own rather than a view of the question: it is where the cycle's
    // price steps are decided, whatever question was posed on Run. Issue 226 (owner ruling
    // 2026-10-07, the proposal's alternative for point 1): a group now, its own page first and the
    // run's calendar — the same steps, laid out by week — second, so the calendar sits beside the
    // decisions it shows rather than as a seventh view of the question.
    {
      labelKey: 'inseason.nav.priceApprovals',
      to: '/inseason/markdown/approvals',
      order: 42.5,
      children: [
        { labelKey: 'inseason.nav.priceApprovalsQueue', to: '/inseason/markdown/approvals', order: 1 },
        { labelKey: 'inseason.nav.markdownCalendar', to: '/inseason/markdown/calendar', order: 2 },
      ],
    },
    // «Stock transfers» ▸ Transfers · Why · Cost to serve is no navigation entry since the owner
    // ruling of 2026-11-02: the transfers and their «Why» are the retired rebalance runs' history
    // (bata-reqs ADR 9002 stage 3, ruling 7), reached only by the links that name a run of them; the
    // routes stay for those links. Cost to serve is live — gate D2 is assessed daily, before the
    // one run, which reads it — so it is an entry of its own, which the Compacting rail lists.
    { labelKey: 'inseason.nav.costToServe', to: '/inseason/cost-to-serve', order: 43.6 },
    // Compacting's one run (bata-reqs ADR 9002 stage 3): its report, and its one merged list of moves —
    // «Approvazioni in attesa», which the rail lists among the app's common entries. The transfer queue
    // that stood here was retired with the legacy writers (ruling 7); its address redirects to the list.
    { labelKey: 'inseason.nav.compactingReport', to: '/inseason/compacting-report', order: 43.4 },
    { labelKey: 'inseason.nav.compactingMoves', to: '/inseason/compacting-moves', order: 43.5 },
    // «Simulazione» (owner rulings 2026-11-02, platform ADR 9056): the one run's simulation, its own page with its
    // own run scope and parameters. New simulation · History are its page's tabs, as Replenishment's (ADR 9023).
    { labelKey: 'inseason.nav.compactingSimulation', to: '/inseason/compacting-simulation', order: 43.45 },
    // The delivery log, the run's execution pack, the market's backlog — one page each.
    {
      labelKey: 'inseason.nav.writeBack',
      to: '/inseason/write-back',
      order: 44,
      children: [
        { labelKey: 'inseason.nav.writeBackLog', to: '/inseason/write-back', order: 1 },
        { labelKey: 'inseason.nav.writeBackPack', to: '/inseason/write-back/pack', order: 2 },
        { labelKey: 'inseason.nav.writeBackBacklog', to: '/inseason/write-back/backlog', order: 3 },
      ],
    },
    // Status, findings and trend of the decision cycles — Management (DEC-87: off the Cockpit).
    {
      labelKey: 'inseason.nav.cycles',
      to: '/inseason/cycles',
      order: 44.2,
      children: [
        { labelKey: 'inseason.nav.cyclesStatus', to: '/inseason/cycles', order: 1 },
        { labelKey: 'inseason.nav.cyclesFindings', to: '/inseason/cycles/findings', order: 2 },
        // Issue 226 (giro 3 «Dopo» point 5): what the run wrote, by kind — off the Findings page.
        { labelKey: 'inseason.nav.cyclesMix', to: '/inseason/cycles/mix', order: 3 },
        { labelKey: 'inseason.nav.cyclesTrend', to: '/inseason/cycles/trend', order: 4 },
      ],
    },
    // What one run read (ADR 6121) — a Management entry (bata-ui#215): it is the run's, across
    // stages, so not Replenishment's, although the store drill links to it with its run.
    { labelKey: 'inseason.nav.runProvenance', to: '/inseason/run-provenance', order: 44.3 },
    // «Perimetri» (bata-reqs ADR 1003, ADR 6028): which stores × articles a replenishment run covers,
    // a market's library — a Management entry, as the run's provenance is: it is set once, not per run.
    { labelKey: 'inseason.nav.runScopes', to: '/inseason/run-scopes', order: 44.35 },
    // Simulate in the rail (bata-ui#166 W2-11): after the queues and their write-back, because a
    // simulation tries a parameter the next cycle would use — it decides nothing on this one.
    // Compose and History are its sub-entries (client rule 3, 2026-09-30); one simulation's
    // results and its proposal are drills from History, not rail entries.
    {
      labelKey: 'inseason.nav.simulate',
      to: '/inseason/simulate',
      order: 44.5,
      children: [
        { labelKey: 'inseason.nav.simulateCompose', to: '/inseason/simulate', order: 1 },
        { labelKey: 'inseason.nav.simulateHistory', to: '/inseason/simulate/history', order: 2 },
      ],
    },
    // Campaign simulation (bata-reqs#33, ADR 6017): advisory, Pricing's — a promotion is a price
    // lever. New campaign and History are its sub-entries; one simulation's results are a drill.
    {
      labelKey: 'inseason.nav.campaigns',
      to: '/inseason/campaigns',
      order: 44.6,
      children: [
        { labelKey: 'inseason.nav.campaignsCompose', to: '/inseason/campaigns', order: 1 },
        { labelKey: 'inseason.nav.campaignsHistory', to: '/inseason/campaigns/history', order: 2 },
      ],
    },
    { labelKey: 'inseason.nav.forecast', to: '/inseason/forecast', order: 45, disabled: true },
    { labelKey: 'inseason.nav.openToBuy', to: '/inseason/open-to-buy', order: 46, disabled: true },
  ],
  routes: [
    // The network-health cockpit (DEC-107, brief part 4, `docs/adr/9046`): four levels, top down —
    // the market, where (by department, store or article), one article in its network (or one
    // store's articles) and one store × article's sizes. Levels 0 and 1 read the network-health
    // read, NOT pinned yet (`src/api/pending/network-health.ts`): «In arrivo» until it is.
    {
      path: 'network-health',
      titleKey: 'inseason.networkHealth.title',
      urlParams: ['by', 'department', 'market', 'runId', 'state', 'view'],
      component: () => import('./network-health/NetworkScreen'),
      surfaces: ['tile', 'chart'],
      tableViewReachable: true,
      exports: { id: 'inseason.networkHealth.network', formats: ['csv', 'xlsx'], resolve: networkExport },
      drill: { to: '/inseason/network-health/by', grain: 'category' },
    },
    {
      path: 'network-health/by',
      titleKey: 'inseason.networkHealth.l1.title',
      urlParams: ['by', 'department', 'market', 'runId', 'state', 'view'],
      component: () => import('./network-health/GroupsScreen'),
      surfaces: ['table'],
      exports: { id: 'inseason.networkHealth.groups', formats: ['csv', 'xlsx'], resolve: groupsExport },
      drill: { to: '/inseason/network-health/article/:articleKey', grain: 'article', alsoTo: ['/inseason/network-health/store/:storeKey'] },
    },
    {
      path: 'network-health/article/:articleKey',
      titleKey: 'inseason.networkHealth.l2.title',
      urlParams: ['by', 'department', 'market', 'runId', 'state', 'view'],
      component: () => import('./network-health/ArticleScreen'),
      surfaces: ['table', 'chart'],
      tableViewReachable: true,
      exports: { id: 'inseason.networkHealth.articleCells', formats: ['csv', 'xlsx'], resolve: cellsExport },
      drill: { to: '/inseason/network-health/store/:storeKey/article/:articleKey', grain: 'store' },
    },
    {
      path: 'network-health/store/:storeKey',
      titleKey: 'inseason.networkHealth.l2.storeTitle',
      urlParams: ['by', 'department', 'market', 'runId', 'state', 'view'],
      component: () => import('./network-health/StoreScreen'),
      surfaces: ['table'],
      exports: { id: 'inseason.networkHealth.storeCells', formats: ['csv', 'xlsx'], resolve: cellsExport },
      drill: { to: '/inseason/network-health/store/:storeKey/article/:articleKey', grain: 'article' },
    },
    {
      path: 'network-health/store/:storeKey/article/:articleKey',
      titleKey: 'inseason.networkHealth.l3.title',
      urlParams: ['by', 'department', 'market', 'runId', 'state', 'view'],
      component: () => import('./network-health/SizesScreen'),
      surfaces: ['table'],
      exports: { id: 'inseason.networkHealth.sizeCells', formats: ['csv', 'xlsx'], resolve: cellsExport },
      // Each size's interventions open what explains them (brief part 4 §1.3; audit 2026-10-21 F9): a
      // proposal's «Perché» and the transfers' «Perché», each a link from its queue; the price
      // decisions; the alert group (ch00's page).
      drill: { to: '/inseason/allocation-why', grain: 'size', alsoTo: ['/inseason/transfer-why', '/inseason/markdown/approvals'] },
    },
    {
      // Issue 308 (ADR 6015): weeks of cover against size availability, one dot per door, over the
      // open run's 13 weeks. A dot — and the table's door link — opens the door's store page.
      path: 'network-health/doors',
      titleKey: 'inseason.doors.title',
      urlParams: ['by', 'department', 'doors', 'market', 'runId', 'state', 'view'],
      component: () => import('./network-health/DoorsScreen'),
      surfaces: ['chart', 'table'],
      tableViewReachable: true,
      exports: { id: 'inseason.networkHealth.doors', formats: ['csv', 'xlsx'], resolve: doorsExport },
      drill: { to: '/inseason/network-health/store/:storeKey', grain: 'store' },
    },
    {
      // Issue 308 (ADR 6015): the service exchange curve of one evaluated forecast run (`?forecastRun=`,
      // the newest evaluated by default under DEC-99).
      path: 'service-exchange',
      titleKey: 'inseason.exchange.title',
      urlParams: ['forecastRun', 'measure'],
      component: () => import('./service-exchange/ExchangeScreen'),
      surfaces: ['chart', 'table'],
      tableViewReachable: true,
      exports: { id: 'inseason.serviceExchange.policies', formats: ['csv', 'xlsx'], resolve: serviceExchangeExport },
      drill: {
        terminal: true,
        reason: 'A policy — one rounding rule at one quantile level over the whole backtest population — is the finest grain the service-exchange read publishes; no per-cell or per-door breakdown of a policy exists to open.',
      },
    },
    {
      path: 'workbench',
      titleKey: 'inseason.workbench.title',
      urlParams: ['overdue', 'row', 'severity'],
      component: () => import('./screens/WorkbenchScreen'),
      surfaces: ['table'],
      exports: { id: 'inseason.workbench.table', formats: ['csv', 'xlsx'], resolve: exceptionQueueExport },
      drill: { to: '/inseason/allocation', grain: 'store', alsoTo: ['/inseason/markdown', '/inseason/rebalancing'] },
    },
    {
      // The run's proposals as one queue (client rule 3, designer items 3.1-3.5): its own page,
      // after the alerts. A line opens in the detail pane beside the queue.
      path: 'workbench/proposals',
      titleKey: 'inseason.workbenchProposals.title',
      urlParams: ['asOf', 'row', 'runId'],
      component: () => import('./screens/WorkbenchProposalsScreen'),
      surfaces: ['table', 'detail'],
      exports: { id: 'inseason.workbenchProposals.table', formats: ['csv', 'xlsx'], resolve: proposalExport },
      // A row's lines open in the detail pane beside the queue; its «Perché» is one link from there (audit 2026-10-21, F9).
      drill: { to: '/inseason/allocation-why', grain: 'store' },
    },
    {
      path: 'forecast',
      titleKey: 'inseason.forecast.title',
      urlParams: [],
      component: () => import('./screens/ForecastScreen'),
      surfaces: ['chart', 'table'],
      tableViewReachable: true,
      exports: { id: 'inseason.forecast.chart', formats: ['csv', 'xlsx'], resolve: accuracyExport },
      drill: { to: '/inseason/forecast/:optionId', grain: 'category', alsoTo: ['/inseason/open-to-buy'] },
    },
    {
      // PROPOSALS — the run's proposed lines (client rule 3, ADR 6006 amendment 2026-09-30). The
      // same address as ever: every link already sent to a planner lands here, and an explain
      // link (`?view=explain`, `?explain=`) is moved to the Why page.
      path: 'allocation',
      titleKey: 'inseason.allocation.title',
      urlParams: ['asOf', 'department', 'explain', 'lines', 'proposalsPanel', 'proposalsView', 'row', 'runId', 'view'],
      component: () => import('./screens/AllocationScreen'),
      // `detail` as well: activating a line opens `ArticleDetailDrawer`, an article-grain
      // surface carrying the product photograph and the whole published record of one
      // proposal (ADR 6001). `chart`: the door × size map of proposed units, which carries
      // its table view.
      surfaces: ['table', 'chart', 'detail'],
      tableViewReachable: true,
      exports: { id: 'inseason.proposals.table', formats: ['csv', 'xlsx'], resolve: proposalExport },
      // bata-ui#148 4.4 (ADR 6006): the target now resolves — one store in the run. Issue 342 §3
      // (ADR 6020): the store and article views' side panel opens «Perché» on a store × article.
      drill: { to: '/inseason/allocation/:storeKey', grain: 'store', alsoTo: ['/inseason/rebalancing', '/inseason/allocation-why'] },
    },
    {
      // EVERY CELL OF THE RUN — «Tutte le celle dell'esecuzione», Proposals' second part (owner ruling
      // 2026-10-24): the run's cells counted by outcome, and the file of every one. It stood under the
      // Proposals table; the two pages carry each other as tabs (`allocation-parts.tsx`). `by`,
      // `department` and `state`: the count links into Network health's list — a link built into another screen's
      // address counts as a read (ADR 9054). The file is the page's export.
      path: 'allocation-cells',
      titleKey: 'inseason.runCells.title',
      urlParams: ['asOf', 'by', 'department', 'row', 'runId', 'state'],
      component: () => import('./screens/AllocationCellsScreen'),
      surfaces: ['table'],
      exports: { id: RUN_CELLS_EXPORT_ID, formats: ['csv', 'xlsx'], resolve: runCellsExport },
      drill: { to: '/inseason/network-health/by', grain: 'store' },
    },
    {
      // WHY — one line's explanation, chosen from the run's lines (ADR 6005 amendment 2026-09-30).
      path: 'allocation-why',
      titleKey: 'inseason.allocationWhy.title',
      urlParams: ['asOf', 'row', 'runId'],
      component: () => import('./screens/AllocationWhyScreen'),
      surfaces: ['table', 'detail'],
      exports: { id: 'inseason.allocationWhy.table', formats: ['csv', 'xlsx'], resolve: allocationWhyExport },
      drill: {
        terminal: true,
        reason: 'A store × article is the finest grain the list publishes; its explanation opens on this page with ?row=<store>|<article>, which is itself a shareable link.',
      },
    },
    {
      // WHY NOT SENT — «Perché»'s second part, «Non inviate» (owner request 2026-10-24, platform ADR 6207):
      // the need the run left without a line, per store × article, and what each size lost to. The two
      // pages carry each other as tabs (`why-parts.tsx`). The file is every size not sent of the run.
      path: 'allocation-why-not-sent',
      titleKey: 'inseason.notSent.title',
      urlParams: ['asOf', 'row', 'runId'],
      component: () => import('./screens/AllocationNotSentScreen'),
      surfaces: ['table', 'detail'],
      exports: { id: NOT_SENT_EXPORT_ID, formats: ['csv', 'xlsx'], resolve: notSentExport },
      drill: {
        terminal: true,
        reason: 'A size not sent is the finest grain the uncovered report publishes; the chosen store × article opens on this page with ?row=<store>|<article>, itself a shareable link.',
      },
    },
    {
      // STOCK — the ledger's size-run map and the units-and-cover grid, as they stood on the
      // allocation screen. The grid `e2e/performance.spec.ts` measures.
      path: 'allocation-stock',
      titleKey: 'inseason.allocationStock.title',
      urlParams: ['asOf', 'row', 'runId', 'stockOut'],
      component: () => import('./screens/AllocationStockScreen'),
      surfaces: ['grid', 'chart'],
      tableViewReachable: true,
      exports: { id: 'inseason.cover.grid', formats: ['csv', 'xlsx'], resolve: coverExport },
      drill: { to: '/inseason/allocation/:storeKey', grain: 'store' },
    },
    {
      // PENDING APPROVALS — the lines with their decision, and the decision panel.
      path: 'allocation-decisions',
      titleKey: 'inseason.allocationDecisions.title',
      urlParams: ['asOf', 'row', 'runId'],
      component: () => import('./screens/AllocationDecisionsScreen'),
      surfaces: ['table', 'form'],
      exports: { id: 'inseason.allocationDecisions.table', formats: ['csv', 'xlsx'], resolve: proposalExport },
      // The canonical replenishment queue: every link to it lands here (audit 2026-10-21, F9); a row's «Perché» is one link away.
      drill: { to: '/inseason/allocation/:storeKey', grain: 'store', alsoTo: ['/inseason/allocation-why'] },
    },
    {
      // The department level of the drill (bata-ui#166 W4-7, ADR 6102): one department family
      // in the run — its lines read with the platform's `department_family` filter, one row per
      // store, each a link to the store page carrying `?department=`. Reached from the Cockpit's
      // department table, not from the rail. Its own prefix, because `allocation/:storeKey`
      // owns every second segment under `allocation/`.
      path: 'departments/:departmentFamily',
      titleKey: 'inseason.department.title',
      urlParams: ['asOf', 'department', 'lines', 'runId'],
      component: () => import('./screens/DepartmentScreen'),
      surfaces: ['table'],
      exports: { id: 'inseason.department.table', formats: ['csv', 'xlsx'], resolve: departmentLinesExport },
      drill: { to: '/inseason/allocation/:storeKey', grain: 'store' },
    },
    {
      // The store level of the drill: the store's proposals (read with `store_key`), its
      // positions and cover, and a size heatmap of what the run proposes there. Reached from
      // the Cockpit's top stores and the allocation drawer — not from the rail (ADR 6006).
      path: 'allocation/:storeKey',
      titleKey: 'inseason.store.title',
      urlParams: ['articles', 'asOf', 'cover', 'department', 'lines', 'runId'],
      component: () => import('./screens/StoreScreen'),
      surfaces: ['table', 'chart'],
      tableViewReachable: true,
      exports: { id: 'inseason.store.table', formats: ['csv', 'xlsx'], resolve: storeLinesExport },
      // What the run read is per run: its own page, linked with the run (ADR 6121 amendment).
      drill: { to: '/inseason/allocation/:storeKey/:articleKey', grain: 'article', alsoTo: ['/inseason/run-provenance'] },
    },
    {
      // The article level: its sizes (the rows), the photograph (ADR 6001), the drivers of the
      // size opened (`?explain=`, ADR 6005) and its positions in the store. A size is opened in
      // place — the finest grain the proposal read publishes; the level below is the article's
      // sales history by week (client rule 3, ADR 6006 amendment 2026-09-30).
      path: 'allocation/:storeKey/:articleKey',
      titleKey: 'inseason.storeArticle.title',
      urlParams: ['asOf', 'department', 'explain', 'lines', 'runId', 'view'],
      component: () => import('./screens/StoreArticleScreen'),
      surfaces: ['table', 'chart', 'detail'],
      tableViewReachable: true,
      exports: { id: 'inseason.storeArticle.table', formats: ['csv', 'xlsx'], resolve: storeArticleExport },
      drill: { to: '/inseason/allocation/:storeKey/:articleKey/sales-history', grain: 'week' },
    },
    {
      // The article's sales and returns by ISO week, over the window the address states (ADR
      // 6101, 6120) — a page below the article, where it was the article page's last panel.
      path: 'allocation/:storeKey/:articleKey/sales-history',
      titleKey: 'inseason.salesHistory.pageTitle',
      urlParams: ['asOf', 'department', 'runId', 'salesBy', 'salesFrom', 'salesTo'],
      component: () => import('./screens/StoreArticleSalesScreen'),
      surfaces: ['table', 'chart'],
      tableViewReachable: true,
      exports: { id: 'inseason.storeArticle.salesHistory', formats: ['csv', 'xlsx'], resolve: salesHistoryExport },
      drill: {
        terminal: true,
        reason: 'A week (or a week and a size) is the finest grain the sales history read publishes, and each is a row of this page.',
      },
    },
    {
      // «Perimetri» (bata-reqs ADR 1003, ADR 6028): the market's library — Scopes, store selections,
      // product selections — each by its newest version; a row opens the entry.
      path: 'run-scopes',
      titleKey: 'inseason.runScopes.library.title',
      urlParams: ['entry', 'kind', 'market'],
      component: () => import('./run-scopes/LibraryScreen'),
      surfaces: ['table'],
      exports: { id: LIBRARY_EXPORT_ID, formats: ['csv', 'xlsx'], resolve: libraryExport },
      drill: { to: '/inseason/run-scopes/:kind/:entryId', grain: 'transaction' },
    },
    {
      // The editor: a new entry, or a new version of one. Saving proposes a change set (ADR 6028).
      // A static segment, so it wins over `run-scopes/:kind/:entryId` (`resolveRoute`, static first).
      path: 'run-scopes/edit',
      titleKey: 'inseason.runScopes.edit.title',
      urlParams: ['entry', 'kind', 'market'],
      component: () => import('./run-scopes/EditScreen'),
      surfaces: ['form'],
    },
    {
      // One library change set: counts before and after, where used; approved in the parameters' queue.
      path: 'run-scopes/changes/:changeId',
      titleKey: 'inseason.runScopes.change.title',
      urlParams: ['market'],
      component: () => import('./run-scopes/ChangeScreen'),
      surfaces: ['table'],
      exports: { id: CHANGE_EXPORT_ID, formats: ['csv', 'xlsx'], resolve: changeExport },
      drill: {
        terminal: true,
        reason: 'A change set is approved or rejected whole (17 §4); each item is one library entry, which its row links to, and its counts are the platform\'s evaluation of that entry, with nothing finer published beneath them.',
      },
    },
    {
      // One entry: its rules in words, what it covers today, where it is used, its versions.
      path: 'run-scopes/:kind/:entryId',
      titleKey: 'inseason.runScopes.entry.title',
      urlParams: ['entry', 'kind', 'market'],
      component: () => import('./run-scopes/EntryScreen'),
      surfaces: ['table'],
      exports: { id: HISTORY_EXPORT_ID, formats: ['csv', 'xlsx'], resolve: historyExport },
      drill: {
        terminal: true,
        reason: 'A version is the finest grain the library keeps: it is appended whole by an approved change set and holds the rules as one document. What a planner wants next from a version is the change set that wrote it, which its row links to.',
      },
    },
    {
      // What one run read — registry values and fingerprint, stock batch, forecast run, what is
      // kept (ADR 6121, 6122). Per run, not per store, so a page of its own; the store page links
      // here with its run. A Management rail entry, not Replenishment's: it is the run's, across stages.
      path: 'run-provenance',
      titleKey: 'inseason.provenance.pageTitle',
      urlParams: ['asOf', 'department', 'runId'],
      component: () => import('./screens/RunProvenanceScreen'),
      surfaces: ['table'],
      exports: { id: 'inseason.provenance.runParameters', formats: ['csv', 'xlsx'], resolve: runProvenanceExport },
      drill: {
        terminal: true,
        reason: 'A registry value the run resolved is the finest grain the provenance read publishes, and each is a row of this page.',
      },
    },
    {
      // An old address of the allocation screen — the same component under another title, with
      // no rail entry and no link to it (client-rules audit 2026-09-30, §2.13). Moved in place to
      // `/inseason/allocation`, the query kept, so a link already sent still lands.
      path: 'proposals',
      titleKey: 'inseason.proposals.title',
      urlParams: [],
      component: () => import('./screens/ProposalsRedirectScreen'),
      surfaces: [],
    },
    {
      path: 'open-to-buy',
      titleKey: 'inseason.openToBuy.title',
      urlParams: [],
      component: () => import('./screens/OpenToBuyScreen'),
      surfaces: ['chart', 'table'],
      tableViewReachable: true,
      exports: { id: 'inseason.openToBuy.chart', formats: ['csv', 'xlsx'], resolve: bridgeExport },
      drill: { to: '/inseason/open-to-buy/:classKey', grain: 'category', alsoTo: ['/inseason/markdown'] },
    },
    {
      // RUN — the question. `/inseason/markdown` stays its address because every link already
      // sent to a planner lands here, and here is where a run is chosen. A form and nothing
      // else since client rule 3 (2026-09-30): the registry-row table it carried is the Pricing
      // tab of the parameter register, and the saved configurations are their own view.
      path: 'markdown',
      titleKey: 'inseason.markdown.title',
      urlParams: ['asOf', 'brandKey', 'channelKey', 'clusterKey', 'compareKind', 'countryKey', 'minorUnit', 'modelVersionId', 'objectiveKind', 'openingUnits', 'optionKey', 'optionKeys', 'penaltyBasis', 'runId', 'salvageFraction', 'weeksRemaining'],
      component: () => import('./screens/MarkdownScreen'),
      surfaces: ['form'],
    },
    {
      // SAVED CONFIGURATIONS — the question on the URL, saved under a name, recalled and
      // compared (ADR 6002, and its amendment of 2026-09-30).
      path: 'markdown/configurations',
      titleKey: 'inseason.runConfig.title',
      urlParams: ['a', 'asOf', 'b', 'optionKey', 'optionKeys', 'runId'],
      component: () => import('./screens/MarkdownConfigurationsScreen'),
      surfaces: ['table'],
      exports: { id: 'inseason.markdown.configurations', formats: ['csv', 'xlsx'], resolve: runConfigurationsExport },
      // Issue 226 (giro 3 «Dopo» point 2): a row opens its configuration on a page of its own, and
      // two rows ticked open the comparison — the list no longer changes shape in place.
      drill: {
        to: '/inseason/markdown/configurations/:configurationId',
        grain: 'transaction',
        alsoTo: ['/inseason/markdown/configurations/compare'],
      },
    },
    {
      // COMPARISON — two saved versions, `?a=<configuration>:<version>&b=…`. A static segment, so it
      // wins over `markdown/configurations/:configurationId` (`resolveRoute`, static first).
      path: 'markdown/configurations/compare',
      titleKey: 'inseason.runConfig.compare.pageTitle',
      urlParams: ['a', 'asOf', 'b', 'optionKeys', 'runId'],
      component: () => import('./screens/MarkdownConfigurationsCompareScreen'),
      surfaces: ['table'],
      exports: { id: 'inseason.markdown.configurationComparison', formats: ['csv', 'xlsx'], resolve: runConfigurationComparisonExport },
      drill: {
        terminal: true,
        reason: 'A parameter of a saved version is the finest grain the configuration store publishes, and each is a row of this page; configurations store questions, not answers, so there is no result below a row to open.',
      },
    },
    {
      // CONFIGURATION — one saved question: its versions, recall, a new version, archive.
      path: 'markdown/configurations/:configurationId',
      titleKey: 'inseason.runConfig.drill.title',
      urlParams: ['a', 'asOf', 'b', 'optionKey', 'optionKeys', 'runId'],
      component: () => import('./screens/MarkdownConfigurationScreen'),
      surfaces: ['table'],
      exports: { id: 'inseason.markdown.configurationVersions', formats: ['csv', 'xlsx'], resolve: runConfigurationVersionsExport },
      drill: { to: '/inseason/markdown/configurations/compare', grain: 'transaction' },
    },
    {
      // PENDING APPROVALS — the run's price steps and the decision recorded on each; the step's
      // "why this" opens in place (`?explain=`, ADR 6005).
      path: 'markdown/approvals',
      titleKey: 'inseason.markdownApprovals.title',
      urlParams: ['asOf', 'compareKind', 'explain', 'markdownView', 'openOption', 'optionKey', 'optionKeys', 'row', 'runId', 'view'],
      component: () => import('./screens/MarkdownApprovalsScreen'),
      surfaces: ['table'],
      exports: { id: 'inseason.markdown.approvals', formats: ['csv', 'xlsx'], resolve: priceActionQueueExport },
      // A price step's own path is the Output view scoped to its option (`explain.tsx`).
      drill: { to: '/inseason/markdown/output', grain: 'article', alsoTo: ['/inseason/markdown/explanation'] },
    },
    {
      // STOCK POSITION — one page of the run's store positions, the position a solve opens from.
      path: 'markdown/stock',
      titleKey: 'inseason.markdownStock.title',
      urlParams: ['asOf', 'runId', 'stock.article', 'stock.store'],
      component: () => import('./screens/MarkdownStockScreen'),
      surfaces: ['table', 'chart'],
      tableViewReachable: true,
      exports: { id: 'inseason.markdown.stockPositions', formats: ['csv', 'xlsx'], resolve: stockPositionExport },
      drill: {
        terminal: true,
        reason: 'A size in one store is the finest grain the stock-position read publishes, and each is a row of this page.',
      },
    },
    {
      // RESULT — the answer to the question posed on Run (issue 226, giro 3 «Dopo» point 1): the
      // path, the objective comparison in layout R's 6 + 6 band, and for several options the list
      // «Options solved» whose rows open one option's answer.
      path: 'markdown/output',
      titleKey: 'inseason.markdownOutput.title',
      urlParams: ['asOf', 'brandKey', 'carry', 'channelKey', 'clusterKey', 'compareKind', 'countryKey', 'explain', 'markdownView', 'minorUnit', 'modelVersionId', 'objectiveKind', 'openOption', 'openingUnits', 'optionKey', 'optionKeys', 'penaltyBasis', 'runId', 'salvageFraction', 'view', 'weeksRemaining'],
      component: () => import('./screens/MarkdownOutputScreen'),
      // `grid` as well: the several-option list mounts a `PlanningGrid`, and ADR 0085 makes
      // `surfaces` a declaration of intent rather than a survey of what a browser happened to
      // render — a several-option scope is reached by URL and is not on screen under e2e.
      surfaces: ['chart', 'grid', 'table'],
      tableViewReachable: true,
      exports: { id: 'inseason.markdown.result', formats: ['csv', 'xlsx'], resolve: markdownResultExport },
      // A row of «Options solved» opens that option's answer here (`?openOption=`); an answer's
      // elasticity is explained on Explanation.
      drill: { to: '/inseason/markdown/output', grain: 'article', alsoTo: ['/inseason/markdown/explanation'] },
    },
    {
      // FRONTIER — margin against cash for the option posed (issue 226). Choosing a strategy writes
      // `?carry=` and opens Result: the follow-up of an action.
      path: 'markdown/frontier',
      titleKey: 'inseason.markdownFrontier.title',
      urlParams: ['asOf', 'brandKey', 'carry', 'channelKey', 'clusterKey', 'compareKind', 'countryKey', 'minorUnit', 'modelVersionId', 'objectiveKind', 'openOption', 'openingUnits', 'optionKey', 'optionKeys', 'penaltyBasis', 'runId', 'salvageFraction', 'weeksRemaining'],
      component: () => import('./screens/MarkdownFrontierScreen'),
      surfaces: ['chart', 'table'],
      tableViewReachable: true,
      exports: { id: 'inseason.markdown.frontier', formats: ['csv', 'xlsx'], resolve: markdownFrontierExport },
      drill: { to: '/inseason/markdown/output', grain: 'article' },
    },
    {
      // THE RUN'S CALENDAR — what the price run recommended, week by week, with why articles are
      // missing under the table (issue 226). Under Pending approvals in the rail.
      path: 'markdown/calendar',
      titleKey: 'inseason.markdownCalendar.title',
      urlParams: ['asOf', 'priceStream', 'runId'],
      component: () => import('./screens/MarkdownCalendarScreen'),
      surfaces: ['table'],
      exports: { id: 'inseason.markdown.calendar', formats: ['csv', 'xlsx'], resolve: markdownCalendarExport },
      // An article opens its sheet in place; its steps are decided on Pending approvals, and a
      // step's own path is Result scoped to its option.
      drill: { to: '/inseason/markdown/approvals', grain: 'article', alsoTo: ['/inseason/markdown/output'] },
    },
    {
      // EXPLANATION — the evidence. The fitted elasticities by cluster, channel and category,
      // one option's response over time, and the refusal register.
      path: 'markdown/explanation',
      titleKey: 'inseason.explain.pageTitle',
      urlParams: ['asOf', 'brandKey', 'channelKey', 'clusterKey', 'compareKind', 'countryKey', 'minorUnit', 'modelVersionId', 'objectiveKind', 'openingUnits', 'optionKey', 'optionKeys', 'penaltyBasis', 'runId', 'salvageFraction', 'weeksRemaining'],
      component: () => import('./screens/MarkdownExplanationScreen'),
      surfaces: ['chart', 'table'],
      tableViewReachable: true,
      exports: { id: 'inseason.elasticity.table', formats: ['csv', 'xlsx'], resolve: elasticityExport },
      drill: { to: '/inseason/markdown', grain: 'sku' },
    },
    {
      // TRANSFERS. Each transfer is drawn once, in its action type's virtualised grid, and every
      // one is reachable (client rule 2, `docs/adr/6000` amendment 2026-09-30); `table` is the
      // by-filter preview's sample.
      path: 'rebalancing',
      titleKey: 'inseason.rebalancing.title',
      urlParams: ['asOf', 'explain', 'row', 'runId', 'sort', 'view'],
      component: () => import('./screens/RebalancingScreen'),
      surfaces: ['table', 'grid', 'chart'],
      tableViewReachable: true,
      exports: { id: 'inseason.rebalancing.table', formats: ['csv', 'xlsx'], resolve: transferExport },
      // The row drill is a click on the grid row (or its label), to the Why page with `?row=<transfer_id>`.
      drill: { to: '/inseason/transfer-why', grain: 'sku', alsoTo: ['/inseason/allocation'] },
    },
    {
      // WHY — one transfer, chosen in the URL (`docs/adr/6005`, amendment of 2026-09-30). It was
      // the `?view=explain` panel of the page above; an old link is replaced by this page.
      // `detail` for the chosen line; `table` for the ranked list beside it (top fifty, ADR 9034),
      // whose file is EVERY filtered transfer with every «why» field — the run's
      // transfer-explanations report, one column per field, an absence empty beside its reason.
      path: 'transfer-why',
      titleKey: 'inseason.transferWhy.title',
      urlParams: ['asOf', 'explain', 'row', 'runId', 'view'],
      component: () => import('./screens/TransferWhyScreen'),
      surfaces: ['table', 'detail'],
      exports: { id: 'inseason.transferWhy.table', formats: ['csv', 'xlsx'], resolve: transferWhyExport },
      // A list row selects its transfer on this page (`?row=`); the one link out is the same
      // transfer on the approvals page.
      drill: { to: '/inseason/transfer-why', grain: 'sku', alsoTo: ['/inseason/compacting-moves'] },
    },
    {
      // The transfer queue's old address (retired by bata-reqs ADR 9002 stage 3, ruling 7): moved in place
      // to the one run's merged list, so a link already sent still lands on the moves to decide.
      path: 'transfer-approvals',
      titleKey: 'inseason.compacting.list.title',
      urlParams: ['moveRunId'],
      component: () => import('./screens/TransferApprovalsRedirectScreen'),
      surfaces: [],
    },
    {
      // THE ONE LIST — every store-to-store move of the run, decided in one approval process (bata-reqs
      // ADR 9002 stage 3 rulings 1 and 2): forced lines first, then every move by value, a held line in
      // its place, the article's state on every row; the candidates refused by name below it.
      path: 'compacting-moves',
      titleKey: 'inseason.compacting.list.title',
      // The linked filters (`docs/adr/9054`): a link from Network health opens the moves of one store ×
      // article on its side (bata-platform ADR 6055 amended, bata-ui ADR 9046 amended); each is a query
      // parameter of `GET /compacting/runs/{run_id}/moves`.
      urlParams: ['moveRunId', 'row', 'f.article', 'f.origin', 'f.destination', 'f.purpose', 'f.state'],
      component: () => import('./screens/CompactingMovesScreen'),
      surfaces: ['table', 'form'],
      exports: { id: MOVE_LIST_EXPORT_ID, formats: ['csv', 'xlsx'], resolve: moveListExport },
      drill: {
        terminal: true,
        reason: 'A move line (origin, destination, article, size) is the finest grain the one run records; its «Perché» is the line itself, opened in the detail beside the list.',
      },
    },
    {
      // THE RUN'S REPORT — figures per article state, ownership, gates, invariants, refusals by reason,
      // the «Perché» workbook and the one simulation of the market (bata-reqs ADR 9002 stage 3).
      path: 'compacting-report',
      titleKey: 'inseason.compacting.report.title',
      urlParams: ['moveRunId'],
      component: () => import('./screens/CompactingReportScreen'),
      surfaces: ['table'],
      exports: { id: MOVE_WORKBOOK_EXPORT_ID, formats: ['csv', 'xlsx'], resolve: moveWorkbookExport },
      drill: { to: '/inseason/compacting-moves', grain: 'sku' },
    },
    {
      // «SIMULAZIONE» — COMPOSE (owner rulings 2026-11-02, platform ADR 9056): the market's one run asked
      // again today over ONE run scope of the library, under an overlay of the admitted parameters.
      path: 'compacting-simulation',
      titleKey: 'inseason.compactingSimulation.title',
      urlParams: ['market', 'moveSimulation', 'moveSimulationFrom'],
      component: () => import('./screens/CompactingSimulationScreen'),
      surfaces: ['form'],
    },
    {
      // HISTORY — the market's simulations, newest first; each row the drill into its own page. A static
      // segment, so it wins over `compacting-simulation/:simulationId`.
      path: 'compacting-simulation/history',
      titleKey: 'inseason.compactingSimulation.history.title',
      urlParams: ['market'],
      component: () => import('./screens/CompactingSimulationHistoryScreen'),
      surfaces: ['table'],
      exports: { id: COMPACTING_SIMULATION_HISTORY_EXPORT_ID, formats: ['csv', 'xlsx'], resolve: compactingSimulationListExport },
      drill: { to: '/inseason/compacting-simulation/:simulationId', grain: 'transaction' },
    },
    {
      // ONE SIMULATION — its state, its question, its run's report and moves, the compare with the live run.
      path: 'compacting-simulation/:simulationId',
      titleKey: 'inseason.compactingSimulation.results.title',
      // The tabs carry the market to the composer and the history.
      urlParams: ['market'],
      component: () => import('./screens/CompactingSimulationResultsScreen'),
      surfaces: ['detail'],
    },
    {
      // PROPOSE — the simulation's overlay as a governed parameter proposal at its market (ADR 9081,
      // amendment of 2026-11-03): change sets in pending approvals, one per owner; nothing is applied.
      path: 'compacting-simulation/:simulationId/propose',
      titleKey: 'inseason.compactingSimulation.propose.title',
      urlParams: ['market'],
      component: () => import('./screens/CompactingSimulationProposeScreen'),
      surfaces: ['form'],
    },
    {
      // COST TO SERVE — gate D2 per market, and nothing else (`docs/adr/0052`, amendment of
      // 2026-09-30). It used to mount the whole transfer screen a second time.
      path: 'cost-to-serve',
      titleKey: 'inseason.costToServe.title',
      urlParams: ['asOf', 'runId'],
      component: () => import('./screens/CostToServeScreen'),
      surfaces: ['table'],
      exports: { id: 'inseason.costToServe.table', formats: ['csv', 'xlsx'], resolve: costToServeExport },
      drill: {
        terminal: true,
        reason: 'A market is the grain gate D2 is evaluated and enabled at: the D2 evidence read publishes one row per market, and the lanes behind its counts are not published as rows.',
      },
    },
    {
      // Simulate (bata-ui#148 5.3, ADR 6007): one stage of a live run, evaluated in a scenario
      // branch under a parameter overlay, compared with the live run, and proposed as a
      // registry change. Reached from the Cockpit and the rail (bata-ui#166 W2-11). Four pages
      // since client rule 3 (2026-09-30, ADR 6007's amendment): COMPOSE here, a form; the
      // run's simulations; one simulation's results; its proposal.
      path: 'simulate',
      titleKey: 'inseason.simulate.title',
      urlParams: ['article', 'from', 'optionKeys', 'runId', 'scenario', 'simulation', 'stage', 'store'],
      component: () => import('./screens/SimulateScreen'),
      surfaces: ['form'],
    },
    {
      // HISTORY — the base run's simulations, each row the drill into its results. A static
      // segment, so it wins over `simulate/:simulationId` (`resolveRoute`, static first).
      path: 'simulate/history',
      titleKey: 'inseason.simulateHistory.title',
      urlParams: ['article', 'from', 'runId', 'scenario', 'simulation', 'stage', 'store'],
      component: () => import('./screens/SimulateHistoryScreen'),
      surfaces: ['table'],
      exports: { id: 'inseason.simulate.history', formats: ['csv', 'xlsx'], resolve: simulationListExport },
      drill: { to: '/inseason/simulate/:simulationId', grain: 'transaction' },
    },
    {
      // RESULTS — one simulation: its status, the comparison with the live run, and what its
      // overlay did to one line. `?simulation=` on the compose page redirects here.
      path: 'simulate/:simulationId',
      titleKey: 'inseason.simulateResults.title',
      urlParams: ['article', 'from', 'runId', 'scenario', 'simulation', 'stage', 'store'],
      component: () => import('./screens/SimulationResultsScreen'),
      surfaces: ['table', 'chart'],
      tableViewReachable: true,
      exports: { id: 'inseason.simulate.table', formats: ['csv', 'xlsx'], resolve: simulationCompareExport },
      drill: {
        terminal: true,
        reason: 'A simulation compares two run digests, which are aggregates; its scenario rows are fenced from every live read, so there is no queue row below a measure to open (bata-platform ADR 6041).',
      },
    },
    {
      // PROPOSE — the simulation's parameters as a registry change, which somebody else approves.
      path: 'simulate/:simulationId/propose',
      titleKey: 'inseason.simulatePropose.title',
      urlParams: [],
      component: () => import('./screens/SimulationProposeScreen'),
      surfaces: ['form'],
    },
    {
      // Campaign simulation — COMPOSE (bata-reqs#33 phase 2, ADR 6017): an envelope and two to six
      // alternatives, launched into the market's sandbox. Advisory: no apply, no approval (D3).
      path: 'campaigns',
      titleKey: 'inseason.campaigns.compose.title',
      urlParams: ['campaignSimulation', 'runId', 'scenario'],
      component: () => import('./campaigns/ComposeScreen'),
      surfaces: ['form'],
    },
    {
      // HISTORY — the base run's campaign simulations; each row the drill into its results.
      path: 'campaigns/history',
      titleKey: 'inseason.campaigns.history.title',
      urlParams: ['runId', 'scenario'],
      component: () => import('./campaigns/HistoryScreen'),
      surfaces: ['table'],
      exports: { id: 'inseason.campaigns.history', formats: ['csv', 'xlsx'], resolve: campaignHistoryExport },
      drill: { to: '/inseason/campaigns/:simulationId', grain: 'transaction' },
    },
    {
      // RESULTS — one campaign simulation's answer: each alternative, the differences against the
      // comparator on realised margin (D4), service as pass or breach, cannibalisation and
      // markdown exposure. Advisory: no action.
      path: 'campaigns/:simulationId',
      titleKey: 'inseason.campaigns.results.title',
      urlParams: ['campaignSimulation', 'runId', 'scenario'],
      component: () => import('./campaigns/ResultsScreen'),
      surfaces: ['table'],
      exports: { id: 'inseason.campaigns.results', formats: ['csv', 'xlsx'], resolve: campaignResultsExport },
      drill: {
        terminal: true,
        reason: 'A campaign simulation answers per alternative over the whole envelope; its lines live in tables of their own that nothing else reads, and no route publishes a door or article row below them (bata-platform ADR 6065).',
      },
    },
    {
      path: 'write-back',
      titleKey: 'inseason.writeBack.title',
      urlParams: ['by', 'needsAttention'],
      component: () => import('./screens/WriteBackScreen'),
      surfaces: ['table'],
      exports: { id: 'inseason.writeBack.table', formats: ['csv', 'xlsx'], resolve: writeBackExport },
      // A command's acknowledgement history is the next level down, and it exists once
      // an O5 adapter is connected to a real target (§14 §8 Q2). Declared as a target
      // rather than as a terminal, because it is a route that will exist.
      drill: { to: '/inseason/write-back/:commandId', grain: 'day' },
    },
    {
      // Whether each decision cycle ran, what it wrote, when it runs next (ADR 9016) — the
      // Cockpit's foot band until DEC-87; the run in the URL names the market.
      path: 'cycles',
      titleKey: 'inseason.cycles.statusTitle',
      urlParams: ['asOf', 'runId'],
      component: () => import('./screens/CyclesScreen'),
      surfaces: ['tile'],
      exports: { id: CYCLE_STATUS_EXPORT_ID, formats: ['csv', 'xlsx'], resolve: cycleStatusExport },
      drill: { to: '/foundations/schedules', grain: 'day' },
    },
    {
      // What the run's digest found (ADR 9019 / 9100) — the Cockpit's until DEC-87. Issue 226
      // (giro 3 «Dopo» point 3): a «Module» selector (`?module=exit`) shows the exit consolidation
      // run's findings here too, moved from Compacting ▸ Consolidation ▸ Estate. The export follows
      // the module (`cycleFindingsExport`: families, or the exit lanes by reason). The drill is the
      // in-season half's; the exit half draws tiles over reason codes and door roles, a grain the
      // digest publishes nothing below, so it opens no level. Issue 226 point 5: «Raggruppa per»
      // (`?by=store`) draws the digest's top stores as one table, each opening its store page.
      path: 'cycles/findings',
      titleKey: 'inseason.cycles.findingsTitle',
      urlParams: ['asOf', 'by', 'department', 'module', 'runId'],
      component: () => import('./screens/CycleFindingsScreen'),
      surfaces: ['tile', 'chart', 'table'],
      tableViewReachable: true,
      exports: { id: CYCLE_FINDINGS_EXPORT_ID, formats: ['csv', 'xlsx'], resolve: cycleFindingsExport },
      drill: { to: '/inseason/departments/:departmentFamily', grain: 'category', alsoTo: ['/inseason/allocation/:storeKey'] },
    },
    {
      // «Composizione» — what the run wrote, by kind (issue 226, giro 3 «Dopo» point 5): the four
      // tiles folded under the Findings list until then, as template S with a «tiles» band (ADR
      // 9038). Run-scoped, `?runId=`, DEC-99 default. Each tile has its table view and CSV.
      path: 'cycles/mix',
      titleKey: 'inseason.cycles.mixTitle',
      urlParams: ['asOf', 'runId'],
      component: () => import('./screens/CycleMixScreen'),
      surfaces: ['tile', 'chart'],
      tableViewReachable: true,
      exports: { id: CYCLE_MIX_EXPORT_ID, formats: ['csv', 'xlsx'], resolve: cycleMixExport },
      drill: {
        terminal: true,
        reason: 'Each tile is one breakdown of the run digest (bata-platform ADR 6039) by family, reason code, transfer purpose or depth band, and the digest publishes nothing below those categories; the department families open their drill from the Findings page, grouped by department.',
      },
    },
    {
      // The digests cycle over cycle (ADR 9100) — folded on the Cockpit until DEC-87.
      path: 'cycles/trend',
      titleKey: 'inseason.cycles.trendTitle',
      urlParams: ['asOf', 'runId'],
      component: () => import('./screens/CycleTrendScreen'),
      surfaces: ['chart', 'table'],
      tableViewReachable: true,
      exports: { id: CYCLE_TREND_EXPORT_ID, formats: ['csv', 'xlsx'], resolve: cycleTrendExport },
      drill: {
        terminal: true,
        reason: 'A trend point is one cycle tick\'s digest figures; the run behind it opens on the Findings page with that run in the URL, which is the level below.',
      },
    },
    {
      // One run's execution pack (ADR 6037) — its own page since package A (client rule 3).
      path: 'write-back/pack',
      titleKey: 'inseason.writeBackPack.title',
      urlParams: ['asOf', 'by', 'group', 'runId', 'sla', 'view'],
      component: () => import('./screens/WriteBackPackScreen'),
      surfaces: ['table'],
      exports: { id: 'inseason.executionPack.lines', formats: ['csv', 'xlsx'], resolve: executionPackExport },
      drill: {
        terminal: true,
        reason: 'A pack line is one approved command, the grain the write-back store records; its delivery history opens in the log, and nothing below a command is published.',
      },
    },
    {
      // What the run's market still owes, from every run (ADR 6104) — its own page (rule 3).
      path: 'write-back/backlog',
      titleKey: 'inseason.writeBackBacklog.title',
      urlParams: ['asOf', 'by', 'group', 'runId', 'sla', 'view'],
      component: () => import('./screens/WriteBackBacklogScreen'),
      surfaces: ['table'],
      exports: { id: 'inseason.backlog.lines', formats: ['csv', 'xlsx'], resolve: backlogExport },
      drill: {
        terminal: true,
        reason: 'A backlog line is one open command of the market, the grain the write-back store records; nothing below a command is published.',
      },
    },
  ],
});
